import { useContext, useEffect, useState } from "react";
import * as yup from "yup";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Container,
  Grid,
} from "@mui/material";

import { AuthContext } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import { LoadingButton } from "@mui/lab";
import DeactivateEmployeeModal from "./DeactivateEmployeeDialog";
import RestoreEmployeeModal from "./RestoreEmployeeDialog";
import IndustryEmployeeForm from "./IndustryEmployeeForm";
import SelectTime from "../Time/SelectTime";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import {
  CheckCircle,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";
import UploadSmsConsentDialog from "./UploadSmsConsentDialog";
import EmployeeDocuments from "./EmployeeDocuments";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const EmployeeDetails = () => {
  const navigate = useNavigate();
  const { openSnackMessage } = useContext(SnackAlertContext);
  const { id } = useParams();
  const { authToken, ezorder, decoded, role } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [uploadSmsConsentDialgoOpen, setUploadSmsConsentDialogOpen] =
    useState(false);
  const [documents, setDocuments] = useState([]);

  // get ID
  const getIndustryEmployee = async () => {
    try {
      setLoading(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      const response = await ezorder.get(`/users/${id}`, data);
      setUserDetails(response.data.employee);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // get User Docs
  const getUserDocuments = async () => {
    try {
      setLoading(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      const response = await ezorder.get(`/users/${id}/documents`, data);
      setDocuments(response.data.userDocuments);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const deactivateEmployeeAccount = async () => {
    try {
      setLoading(true);

      const response = await ezorder.delete(`/users/${userDetails.id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response.status == 200 || response.status == 201) {
        openSnackMessage("success", "User Account Deactivated");
        navigate("/employees");
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const restoreEmployeeAccount = async () => {
    try {
      setLoading(true);

      const response = await ezorder.put(
        `/users/${userDetails.id}/restore`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      openSnackMessage("success", "User Account Restored");
      navigate("/employees");
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getUserDocuments();
      getIndustryEmployee();
    }
  }, [id]);

  if (userDetails) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" sx={{ textAlign: "center" }}>
          Employee Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item md={12}>
            <Typography>Full Name: {userDetails.fullName}</Typography>
            <Typography>Email: {userDetails.email}</Typography>
            <Typography>Phone: {userDetails.phone}</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography>SMS Service Opt-In:</Typography>{" "}
              {userDetails.isSubscribedToSmsService ? (
                <CheckCircleOutline
                  color="success"
                  onClick={() => {
                    setUploadSmsConsentDialogOpen(true);
                  }}
                />
              ) : (
                <ErrorOutline
                  color="error"
                  onClick={() => {
                    setUploadSmsConsentDialogOpen(true);
                  }}
                />
              )}
            </Box>
          </Grid>

          <Grid item md={12}>
            <Typography>Job Title: {userDetails.jobTitle}</Typography>
            <Typography>App Role: {userDetails.role}</Typography>
            <Typography>Salary Type: {userDetails.salaryType}</Typography>
          </Grid>

          {userDetails.salaryType == "HOURLY" && (
            <Grid item md={12}>
              <Typography>
                Start Time:{" "}
                {userDetails && userDetails.workStartTime
                  ? dayjs(`2018-01-01 ${userDetails.workStartTime}`).format(
                      "hh:mm a"
                    )
                  : ""}
              </Typography>
              <Typography>
                Stop Time:{" "}
                {userDetails &&
                userDetails.workStartTime &&
                userDetails.workDurationMinutes
                  ? dayjs(`2018-01-01 ${userDetails.workStartTime}`)
                      .add(userDetails.workDurationMinutes, "minutes")
                      .format("hh:mm a")
                  : ""}
              </Typography>
              <Typography>
                Lunch Duration:{" "}
                {userDetails.lunchDurationMinutes
                  ? userDetails.lunchDurationMinutes
                  : `0`}{" "}
                minutes
              </Typography>
            </Grid>
          )}
        </Grid>
        <Box>
          <LoadingButton
            variant="outlined"
            onClick={() => {
              navigate(`/employees/${id}/edit`);
            }}
            sx={{ marginRight: "8px" }}
          >
            Edit Employee Details
          </LoadingButton>

          {/* Activate-Deactivate */}
          {userDetails.deletedAt ? (
            <LoadingButton
              variant="outlined"
              color="success"
              onClick={() => {
                setRestoreDialogOpen(true);
              }}
            >
              Restore Account
            </LoadingButton>
          ) : (
            <LoadingButton
              variant="outlined"
              color="error"
              onClick={() => {
                setDeactivateDialogOpen(true);
              }}
            >
              Deactivate Account
            </LoadingButton>
          )}
        </Box>

        <EmployeeDocuments userId={id} documents={documents} />

        <DeactivateEmployeeModal
          open={deactivateDialogOpen}
          handleClose={() => {
            setDeactivateDialogOpen(!deactivateDialogOpen);
          }}
          loading={loading}
          onConfirm={() => {
            deactivateEmployeeAccount();
          }}
        />
        <RestoreEmployeeModal
          open={restoreDialogOpen}
          handleClose={() => {
            setRestoreDialogOpen(!restoreDialogOpen);
          }}
          loading={loading}
          onConfirm={() => {
            restoreEmployeeAccount();
          }}
        />

        <UploadSmsConsentDialog
          employeeId={userDetails.id}
          phone={userDetails.phone}
          open={uploadSmsConsentDialgoOpen}
          onClose={() => {
            setUploadSmsConsentDialogOpen(false);
          }}
          onSuccess={() => {
            setUploadSmsConsentDialogOpen(false);
            getIndustryEmployee();
            getUserDocuments();
          }}
          // downloadName={`SIGNED-CUSTOMER-QUOTE-${quote.id}-${quote.company.name}-${quote.project.name}.pdf`}
        />
      </Box>
    );
  }
};

export default EmployeeDetails;
